/**
 * In-memory cache for GitHub markdown content
 * Cache is cleared on server restart or when explicitly refreshed
 */

import type { GitHubFile } from './fetch'

type CachedContent = {
  content: string
  lastFetched: number
  etag?: string
}

type CachedFileList = {
  files: GitHubFile[]
  lastFetched: number
}

type CacheEntry = {
  [key: string]: CachedContent | CachedFileList
}

// Type guard to check if entry is CachedContent
function isCachedContent(entry: CachedContent | CachedFileList): entry is CachedContent {
  return 'content' in entry
}

// Type guard to check if entry is CachedFileList
function isCachedFileList(entry: CachedContent | CachedFileList): entry is CachedFileList {
  return 'files' in entry
}

class GitHubCache {
  private cache: CacheEntry = {}
  private readonly CACHE_DURATION = 1000 * 60 * 60 // 1 hour default

  /**
   * Get cached content if it exists and is still valid
   */
  get(key: string): CachedContent | null {
    const entry = this.cache[key]
    if (!entry || !isCachedContent(entry)) {
      return null
    }

    const age = Date.now() - entry.lastFetched
    if (age > this.CACHE_DURATION) {
      // Cache expired, but return it anyway (stale-while-revalidate pattern)
      return entry
    }

    return entry
  }

  /**
   * Set cache entry
   */
  set(key: string, content: string, etag?: string): void {
    this.cache[key] = {
      content,
      lastFetched: Date.now(),
      etag,
    }
  }

  /**
   * Clear specific cache entry
   */
  clear(key: string): void {
    delete this.cache[key]
  }

  /**
   * Clear all cache
   */
  clearAll(): void {
    this.cache = {}
  }

  /**
   * Check if cache entry exists
   */
  has(key: string): boolean {
    return key in this.cache
  }

  /**
   * Get all cache keys
   */
  keys(): string[] {
    return Object.keys(this.cache)
  }

  /**
   * Get cached file list
   */
  getFileList(key: string): CachedFileList | null {
    const entry = this.cache[key]
    if (!entry || !isCachedFileList(entry)) {
      return null
    }
    return entry
  }

  /**
   * Set cached file list
   */
  setFileList(key: string, files: GitHubFile[]): void {
    this.cache[key] = {
      files,
      lastFetched: Date.now(),
    }
  }
}

// Singleton instance
export const githubCache = new GitHubCache()
